import React, { useEffect } from 'react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import { useTheme } from '@mui/material/styles';
import { Order } from '../../../../app/services/api/orderManagementClient';
import { RetailerHeaderModel } from '../../../../app/services/api/ofdClient';
import useCurrencyCodeConfiguration from '../../../../hooks/configurations/useCurrencyCodeConfiguration';
import { applyRoundAndFormat } from '../../../../utils';

interface TopSellingProductsByMarketProps {
  orders: Order[];
  retailers: RetailerHeaderModel[];
}

const TopSellingProductsByMarket = ({ orders, retailers }: TopSellingProductsByMarketProps) => {
  const theme = useTheme();
  const { currencyCode, getCurrencyCodeConfiguration } = useCurrencyCodeConfiguration();

  useEffect(() => {
    getCurrencyCodeConfiguration();
  }, []);

  // Aggregate sales by supplierName (market) and product (using orderType as a stand-in)
  const marketProductTotals: { [market: string]: { [product: string]: number } } = {};
  orders.forEach(order => {
    if (!marketProductTotals[order.supplierName]) marketProductTotals[order.supplierName] = {};
    marketProductTotals[order.supplierName][order.orderType] = (marketProductTotals[order.supplierName][order.orderType] || 0) + (order.totalCost || 0);
  });
  // For each market, get the top product
  const topProducts = Object.entries(marketProductTotals).map(([market, products]) => {
    const sorted = Object.entries(products).sort((a, b) => b[1] - a[1]);
    if (sorted.length === 0) return null;
    const [product, total] = sorted[0];
    // Find retailer for this market
    const retailer = retailers.find(r => r.franchiseName === market);
    return { market, product, total, lat: retailer?.latitude, lng: retailer?.longitude };
  }).filter(Boolean);

  const data = topProducts.map((p) => ({ name: p?.market, value: p?.total, details: `${p?.market} - ${applyRoundAndFormat((p?.total ?? 0) / 1000, currencyCode)}k` }));

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

  // Custom label renderer to prevent truncation
  const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, index }: { cx: number, cy: number, midAngle: number, innerRadius: number, outerRadius: number, percent: number, index: number }) => {
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 1.1;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    const name = data[index].name;
    return (
      <text
        x={x}
        y={y}
        fill={theme.palette.text.primary}
        textAnchor={x > cx ? 'start' : 'end'}
        dominantBaseline="central"
        style={{ fontSize: theme.typography.textSM?.fontSize || 12, fontFamily: theme.typography.fontFamily }}
      >
        {`${name} (${(percent * 100).toFixed(0)}%)`}
      </text>
    );
  };

  // Optionally, you can use the lat/lng for map visualizations here

  return (
    <ResponsiveContainer width="100%"
      height={200}>
      <PieChart>
        <Pie
          data={data}
          cx="50%"
          cy="50%"
          labelLine={false}
          label={renderCustomLabel}
          outerRadius={100}
          fill={theme.palette.primary.main}
          dataKey="value"
        >
          {data.map((entry, index) => (
            <Cell key={`cell-${index}`}
              fill={COLORS[index % COLORS.length]} />
          ))}
        </Pie>
        <Tooltip formatter={(value, name, props) => [applyRoundAndFormat(value as number, currencyCode), `${props.payload.details}`]} />
      </PieChart>
    </ResponsiveContainer>
  );
};

export default TopSellingProductsByMarket;